<script setup>
import {useRoute} from "vue2-helpers/vue-router";
import needsService from "@/services/needs.service";
import {ref, onMounted, provide, computed} from "vue";
import needsStartupsService from "@/services/needs-startups.service";
import SearchMatching from "@/views/needs/need/SearchMatching.vue";
import Solutions from "@/views/needs/need/solutions/Solutions.vue";
import NewMatches from "@/views/needs/need/NewMatches.vue";
import VClamp from "vue-clamp";
import Activities from "@/views/needs/need/Activities.vue";
import NeedEditModal from "@/views/needs/need/NeedEditModal.vue";
import Escalate from "@/views/needs/need/Escalate.vue";
import NeedReport from "@/views/needs/need/NeedReport.vue";

const steps = [
    'Add suggested solutions',
    'Rate all your solutions',
    'Escalate need to innovation team'
]

const route = useRoute();
const need = ref(null);
const startups = ref([]);
const activities = ref([]);
const searchMatching = ref(null);
const needEditModal = ref(null);
const newMatches = ref(null);
const isSharedWithEcosystems = ref(null);

const solutions = computed(() => startups.value.filter(startup => startup.status === 'liked'));
const escalated = computed(() => need.value.status === 3);

onMounted(() => {
    getInfo();
});

async function getInfo() {
    const {data} = await needsService.getInfo(route.params.id);
    need.value = data;
    isSharedWithEcosystems.value = !!need.value.is_shared_with_ecosystems;

    if (!need.value.start_search) {
        getStartups();
        getActivities();
    }
}

async function getStatus() {
    const {data} = await needsService.getStatus(need.value.id);

    need.value.status = data;
}

async function getActivities() {
    const {data} = await needsService.getActivities(need.value.id);

    activities.value = data;
}

async function getStartups() {
    const {data} = await needsStartupsService.getStartups(need.value.id);
    startups.value = data;
}

function updateRating(linkId, rating) {
    const startup = startups.value.find(s => s.link_id === linkId);

    if (startup) {
        startup.rating = rating;
    }

    getStatus();
}

async function setEcosystemsVisibility(type) {
    await needsService.setEcosystemsVisibility(need.value.id, type);
    getInfo();
}

function evaluate() {
    newMatches.value?.evaluate();
}

provide('need', need);
provide('getInfo', getInfo);
provide('getStatus', getStatus);
provide('getStartups', getStartups);
provide('updateRating', updateRating);
provide('getActivities', getActivities);
provide('solutions', solutions);
provide('escalated', escalated);
</script>

<template>
    <div v-if="need" class="need-page">
        <!-- <div class="card-needs">
            <div class="card-needs__content card-needs__content--columns">
                <div>
                </div>
            </div>
        </div> -->

        <div class="need-page__content">
            <aside class="need-page__aside">
                <h4 class="heading4">{{need.name}}</h4>

                <p>
                    <v-clamp autoresize :max-lines="4" class="activity-list__content__text" style="white-space: pre-wrap">
                        {{need.description}}

                        <template #after="{ toggle, expanded, clamped }">
                            <a v-if="expanded" class="link-normal" @click="toggle">less</a>
                            <a v-if="clamped" class="link-normal" @click="toggle">more</a>
                        </template>
                    </v-clamp>
                </p>

                <div v-if="!escalated" class="mb-4">
                    <a class="link link-edit-light" @click="needEditModal.open(need.id)">Refine</a>
                </div>

                <div class="card-needs__sidebar mb-4">
                    <ul class="list-plan">
                        <li
                            v-for="(step, index) in steps"
                            :key="index"
                            :class="{'list-plan__done': need.status > index}"
                        >
                            {{step}}
                        </li>
                    </ul>

                    <Escalate/>
                </div>

                <NeedReport v-if="solutions.length >= 5"/>

                <div v-if="activities.length" class="card-headline">
                    <header class="card-headline__header">Activity</header>

                    <Activities
                        :activities="activities"
                    />
                </div>

                <div class="card-headline">
                    <header class="card-headline__header">Visibility Settings</header>

                    <div class="card-headline__content">
                        <div class="input-radio mb-4">
                            <input
                                type="radio"
                                v-model="isSharedWithEcosystems"
                                :value="true"
                                name="sinp"
                                id="s1"
                                @change="setEcosystemsVisibility(true)"
                            >
                            <label for="s1">
                                <div class="input-radio__inner">
                                    AI + Network Partners (Recommended)
                                    <p>Get insights from SwitchPitch AI and our venture ecosystem partners</p>
                                </div>
                            </label>
                        </div>

                        <div class="input-radio">
                            <input
                                type="radio"
                                v-model="isSharedWithEcosystems"
                                :value="false"
                                name="sinp"
                                id="s2"
                                @change="setEcosystemsVisibility(false)"
                            >
                            <label for="s2">
                                <div class="input-radio__inner">
                                    AI Only
                                    <p>Your request will not be shared with ecosystem partners</p>
                                </div>
                            </label>
                        </div>                        
                    </div>
                </div>
            </aside>

            <SearchMatching
                v-if="need?.need_matching_search"
                :need-id="need.id"
                ref="searchMatching"
                @done="getInfo"
            />

            <div v-else class="need-page__inner">
                <NewMatches v-if="!escalated" ref="newMatches"/>

                <Solutions @evaluate="evaluate"/>
            </div>
        </div>

        <NeedEditModal ref="needEditModal"/>
    </div>
</template>

<style scoped>

</style>
